import { useState, useEffect } from 'react';
import { useGameSocket } from '../hooks/useGameSocket';
import { useGameStore } from '../store/gameStore';
import { useSound } from '../contexts/SoundContext';
import { AdminHeader } from '../components/admin/AdminHeader';
import { MiniScoreboard } from '../components/admin/MiniScoreboard';
import { QuestionCard } from '../components/admin/QuestionCard';
import { SubjectSelector } from '../components/admin/SubjectSelector';
import { RouletteSelector } from '../components/RouletteSelector';
import { TimerDisplay } from '../components/game/TimerDisplay';
import { type Subject, type TeamId, TEAM_COLORS, TEAM_NAMES } from '../types/game';

const API = import.meta.env.VITE_API_URL || '/api';
const QUESTIONS_PER_ROUND = 3;

interface Props { gameId: string }

export function AdminView({ gameId }: Props) {
  const { send } = useGameSocket(gameId, null, 'admin');
  const { game, connected } = useGameStore();
  const sound = useSound();

  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [selectedAnswer, setSelectedAnswer] = useState<string | undefined>();
  const [confirmAction, setConfirmAction] = useState<{ type: 'reset' | 'grade'; payload?: string; msg: string } | null>(null);

  useEffect(() => {
    fetch(`${API}/subjects?grade=${game.grade}`)
      .then(r => r.json())
      .then(data => setSubjects(data as Subject[]))
      .catch(() => { });
  }, [game.grade]);

  useEffect(() => {
    if (game.subjects.length > 0) setSubjects(game.subjects);
  }, [game.subjects]);

  useEffect(() => {
    if (game.winner || game.roundPhase === 'round_end') {
      setSelectedAnswer(undefined);
    }
  }, [game.winner, game.roundPhase]);

  const handleReset = () => {
    setConfirmAction({ type: 'reset', msg: '¿Estás completamente seguro de reiniciar TODO el juego? Esto borrará puntajes, turnos y progreso.' });
  };

  const handleChangeGrade = (g: string) => {
    if (g === game.grade) return;
    setConfirmAction({ type: 'grade', payload: g, msg: `¿Cambiar al grado ${g}°? Esto reiniciará el estado de la ronda actual.` });
  };

  const confirmActionExecute = () => {
    if (!confirmAction) return;
    if (confirmAction.type === 'reset') send({ type: 'ADMIN_RESET' });
    if (confirmAction.type === 'grade') send({ type: 'ADMIN_SET_GRADE', grade: confirmAction.payload! });
    setConfirmAction(null);
  };

  const handleSelectSubject = (subjectId: number) => {
    send({ type: 'ADMIN_SELECT_SUBJECT', subjectId });
  };

  const handleTurnAnswer = (correct: boolean) => {
    send({ type: 'ADMIN_SUBMIT_TURN_ANSWER', correct });
    sound.play(correct ? 'correct' : 'incorrect');
  };

  const handleStealAnswer = (correct: boolean) => {
    send({ type: 'ADMIN_CONFIRM_ANSWER', correct });
    sound.play(correct ? 'correct' : 'incorrect');
  };

  const currentTurnTeam = game.teamOrder?.[game.currentTurnIndex ?? 0] as TeamId | undefined;
  const currentTurnColor = currentTurnTeam ? TEAM_COLORS[currentTurnTeam] : null;
  const isStealMode = game.roundPhase === 'steal' && game.status === 'steal_mode';
  const isRoundEnd = game.roundPhase === 'round_end' && game.status === 'round_end';
  const isIncorrectAnswer = game.status === 'incorrect_answer';
  const hasWinner = !!game.winner;
  const questionsLeft = QUESTIONS_PER_ROUND - game.questionsThisRound;
  const isRoundComplete = questionsLeft <= 0;

  return (
    <div className="min-h-screen bg-bg-deep bg-grid-tech flex flex-col">
      {confirmAction && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="card p-8 max-w-md w-full text-center shadow-[0_0_50px_rgba(239,68,68,0.3)] border-error" style={{ animation: 'bounce-in 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)' }}>
            <h2 className="text-2xl font-black text-error mb-4">⚠️ Confirmación</h2>
            <p className="text-lg mb-8 text-white/90">{confirmAction.msg}</p>
            <div className="grid grid-cols-2 gap-4">
              <button className="btn btn-outline" onClick={() => setConfirmAction(null)}>Cancelar</button>
              <button className="btn btn-error" onClick={confirmActionExecute}>Sí, Continuar</button>
            </div>
          </div>
        </div>
      )}

      <AdminHeader game={game} connected={connected} onReset={handleReset} questionsPerRound={QUESTIONS_PER_ROUND} />

      <main className="flex-1 p-6 grid grid-cols-[280px_1fr] gap-6">
        <aside className="flex flex-col gap-4">
          <div className="card">
            <h3 className="text-xs font-bold text-muted uppercase tracking-wider mb-3">Grado</h3>
            <div className="flex gap-2">
              {['1', '2', '3'].map(g => (
                <button
                  key={g}
                  onClick={() => handleChangeGrade(g)}
                  className={game.grade === g ? 'btn btn-sm btn-primary flex-1' : 'btn btn-sm btn-outline flex-1'}
                >
                  {g}°
                </button>
              ))}
            </div>
          </div>

          <div className="card">
            <h3 className="text-xs font-bold text-muted uppercase tracking-wider mb-3">Muerte Súbita</h3>
            <button
              onClick={() => send({ type: 'ADMIN_TOGGLE_SUDDEN_DEATH' })}
              className={`btn w-full font-bold ${game.isSuddenDeath ? 'bg-error text-white shadow-[0_0_15px_rgba(239,68,68,0.5)] border-error' : 'btn-outline'}`}
            >
              {game.isSuddenDeath ? '🔥 Desactivar' : 'Activar Desempate'}
            </button>
          </div>

          <MiniScoreboard scores={game.scores} />




          {game.timerExpiresAt ? (
            <div className="card">
              <TimerDisplay
                expiresAt={game.timerExpiresAt}
                className="text-4xl font-black text-error text-center tabular-nums"
              />
              <button
                onClick={() => send({ type: 'ADMIN_STOP_TIMER' })}
                className="btn btn-sm btn-danger w-full mt-3"
              >
                Detener Tiempo
              </button>
            </div>
          ) : (
            (game.status === 'team_answering' || game.status === 'steal_mode' || game.status === 'question_active') && (
              <div className="card">
                <h3 className="text-xs font-bold text-muted uppercase tracking-wider mb-3">Temporizador</h3>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => send({ type: 'ADMIN_START_TIMER', seconds: 30 })}
                    className="btn btn-sm btn-success"
                  >
                    30s
                  </button>
                  <button
                    onClick={() => send({ type: 'ADMIN_START_TIMER', seconds: 15 })}
                    className="btn btn-sm btn-success"
                  >
                    15s
                  </button>
                  <button
                    onClick={() => send({ type: 'ADMIN_START_TIMER', seconds: 10 })}
                    className="btn btn-sm btn-success"
                  >
                    10s
                  </button>
                </div>
                <button
                  onClick={() => {
                    const secs = prompt('Segundos:', '10');
                    if (secs) send({ type: 'ADMIN_START_TIMER', seconds: parseInt(secs) });
                  }}
                  className="btn btn-sm btn-outline w-full mt-2"
                >
                  Personalizado...
                </button>
              </div>
            )
          )}
        </aside>

        <main className="flex flex-col gap-4">
          {game.roundPhase === 'roulette' ? (
            <div className="card flex-1 flex flex-col items-center justify-center min-h-[500px]">
              <RouletteSelector
                teamOrder={game.teamOrder}
                onSpin={() => send({ type: 'ADMIN_SPIN_ROULETTE' })}
                onStartRound={() => send({ type: 'ADMIN_START_ROUND' })}
                hasQuestion={!!game.currentQuestion}
              />
            </div>
          ) : !game.currentQuestion ? (
            <div className="card p-8 flex flex-col items-center justify-center min-h-[400px]">
              <h2 className="text-2xl font-bold mb-6">Selecciona la materia</h2>

              {subjects.length === 0 ? (
                <p className="text-muted">Cargando...</p>
              ) : game.usedSubjects.length >= subjects.length || isRoundComplete ? (
                <div className="text-center">
                  <p className="text-warning mb-4">Ronda {Math.max(1, game.round)} completa</p>
                  <button
                    onClick={() => send({ type: 'ADMIN_NEXT_ROUND' })}
                    className="btn btn-primary btn-lg"
                  >
                    Siguiente Ronda
                  </button>
                </div>
              ) : (
                <SubjectSelector
                  subjects={subjects}
                  selectedId={game.currentSubject?.id ?? null}
                  usedSubjects={game.usedSubjects}
                  onSelect={handleSelectSubject}
                />
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <QuestionCard
                question={game.currentQuestion}
                selectedAnswer={selectedAnswer}
                onSelectAnswer={setSelectedAnswer}
                showOptions={hasWinner || isStealMode}
                subjectColor={game.currentSubject?.color}
              />

              {isRoundEnd && (
                <div className="card p-6 bg-success text-black border-2 border-white shadow-[4px_4px_0_white]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl font-black">✓</span>
                      <div>
                        <div className="font-black text-lg uppercase tracking-wider">Pregunta completada</div>
                        <div className="text-sm font-bold opacity-80">Ronda {Math.max(1, game.round)} · {game.questionsThisRound}/{QUESTIONS_PER_ROUND} preguntas</div>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      {!isRoundComplete && (
                        <button
                          onClick={() => send({ type: 'ADMIN_NEXT_QUESTION' })}
                          className="btn btn-primary"
                        >
                          Siguiente Pregunta
                        </button>
                      )}
                      {isRoundComplete && (
                        <button
                          onClick={() => send({ type: 'ADMIN_NEXT_ROUND' })}
                          className="btn btn-primary"
                        >
                          Siguiente Ronda
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {game.isSuddenDeath && !hasWinner && !isStealMode && !isRoundEnd && (
                <div className="card p-6 text-center border-error shadow-[0_0_30px_rgba(239,68,68,0.15)]">
                  <div className="mb-6">
                    <h3 className="text-2xl font-black text-error uppercase tracking-wider animate-pulse">
                      ¡Muerte Súbita!
                    </h3>
                    <p className="text-muted text-sm mt-2 font-medium">
                      Lee la pregunta y abre el buzzer cuando estés listo.
                    </p>
                  </div>
                  <button
                    onClick={() => send({ type: 'ADMIN_OPEN_BUZZER' })}
                    className="btn btn-error btn-xl py-8 text-2xl font-black w-full"
                  >
                    🔔 Abrir Buzzer a Todos
                  </button>
                </div>
              )}

              {!game.isSuddenDeath && !isStealMode && !isRoundEnd && !hasWinner && currentTurnTeam && (
                <div className="card p-6">
                  <div className="text-center mb-2">
                    <div className="text-xs font-bold text-muted uppercase tracking-wider">
                      Turno de {TEAM_NAMES[currentTurnTeam]}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => handleTurnAnswer(true)}
                      className="btn btn-success btn-xl py-8 text-xl font-black"
                    >
                      ✓ Correcto
                    </button>
                    <button
                      onClick={() => handleTurnAnswer(false)}
                      className="btn btn-danger btn-xl py-8 text-xl font-black"
                    >
                      ✗ Incorrecto
                    </button>
                  </div>

                  <div className="text-xs text-muted text-center mt-3">
                    {questionsLeft} {questionsLeft === 1 ? 'pregunta restante' : 'preguntas restantes'} en esta ronda
                  </div>
                </div>
              )}

              {isIncorrectAnswer && (
                <div className="card p-6 border-error shadow-[0_0_30px_rgba(239,68,68,0.15)] text-center">
                  <div className="mb-6">
                    <div className="text-xs font-bold text-muted uppercase tracking-wider mb-2">
                      Intento Fallido
                    </div>
                    {game.winner && (
                      <div
                        className="flex items-center gap-3 px-4 py-3 rounded-xl mx-auto w-fit border border-error bg-error/10 mb-3"
                      >
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-lg font-black text-white"
                          style={{ background: TEAM_COLORS[game.winner.team].bg }}
                        >
                          {game.winner.team}
                        </div>
                        <span className="font-black text-error">
                          {TEAM_NAMES[game.winner.team]} respondió incorrectamente
                        </span>
                      </div>
                    )}
                    <p className="text-muted text-sm font-medium">
                      ¿Qué deseas hacer ahora?
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {game.teamQueue.length > 0 ? (
                      <button
                        onClick={() => send({ type: 'ADMIN_STEAL' })}
                        className="btn btn-warning btn-xl py-6 text-lg font-bold"
                      >
                        ⚡ Activar Modo Robo
                      </button>
                    ) : (
                      <div className="btn btn-disabled btn-xl py-6 text-lg font-bold opacity-50 flex items-center justify-center border-dashed border-2 border-white/10 text-muted">
                        Sin equipos para robo
                      </div>
                    )}
                    
                    <button
                      onClick={() => send({ type: 'ADMIN_FINISH_QUESTION' })}
                      className="btn btn-outline btn-xl py-6 text-lg font-bold"
                    >
                      👁️ Revelar Respuesta
                    </button>
                  </div>
                </div>
              )}

              {!isIncorrectAnswer && (hasWinner || isStealMode) && !isRoundEnd && (
                <div className="card p-6">
                  {hasWinner && (
                    <div className="mb-4">
                      <div className="text-xs font-bold text-muted uppercase tracking-wider mb-2 text-center">
                        {game.isSuddenDeath ? 'Muerte Súbita - Respondiendo' : isStealMode ? 'Robando' : 'Respondiendo'}
                      </div>
                      <div
                        className="flex items-center gap-3 px-4 py-3 rounded-xl mx-auto w-fit"
                        style={{ background: `${TEAM_COLORS[game.winner!.team].bg}20` }}
                      >
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-lg font-black text-white"
                          style={{ background: TEAM_COLORS[game.winner!.team].bg }}
                        >
                          {game.winner!.team}
                        </div>
                        <span className="font-bold" style={{ color: TEAM_COLORS[game.winner!.team].bg }}>
                          {TEAM_NAMES[game.winner!.team]}
                        </span>
                      </div>
                    </div>
                  )}

                  {hasWinner && (
                    <div>
                      <div className="grid grid-cols-2 gap-4">
                        <button
                          onClick={() => handleStealAnswer(true)}
                          className="btn btn-success btn-xl py-6 text-lg font-bold"
                        >
                          ✓ Correcto
                        </button>
                        <button
                          onClick={() => handleStealAnswer(false)}
                          className="btn btn-danger btn-xl py-6 text-lg font-bold"
                        >
                          ✗ Incorrecto
                        </button>
                      </div>
                    </div>
                  )}

                  {isStealMode && !hasWinner && (
                    <div className="text-center">
                      <div className="text-warning font-bold text-lg mb-4 animate-pulse">
                        Esperando que un equipo presione el buzzer...
                      </div>
                      <button
                        onClick={() => {
                          send({ type: 'ADMIN_CLOSE_BUZZER' });
                          send({ type: 'ADMIN_NEXT_QUESTION' });
                        }}
                        className="btn btn-outline btn-lg"
                      >
                        {game.isSuddenDeath ? 'Saltar pregunta' : 'Saltar pregunta (sin robo)'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </main>
      </main>
    </div>
  );
}
